import { type LocalDate, Money, addMonths, compareDates, daysBetween, dec } from "@despejo/domain";

/**
 * Penalty and interest ESTIMATES for late filing/payment (plan §12.2,
 * docs/tax/PENALTIES.md). Hacienda's amount is authoritative; these numbers
 * exist to show exposure and urgency.
 *
 *  - Art. 79 CNPT: late filing fine = 0.5 salario base per return (nil returns included).
 *  - Art. 80 bis: late payment of self-assessed tax = 1% per month or fraction, cap 20%.
 *  - Art. 57: interest, simple, actual/365 from the day after the due date, at the
 *    rate in force on each day (rates change by Hacienda resolution).
 *  - Art. 88: voluntary regularization reduces the FINE only (75%, or 80% if the
 *    fine is self-assessed and paid together) — never the surcharge or interest.
 *  - Art. 83: omitted informative return (270/272): 2% of prior-year gross income,
 *    min 3 / max 100 salarios base; 10 salarios base if income is unknown.
 */

export interface ParameterRow {
  key: string;
  effective_from: LocalDate;
  effective_to: LocalDate | null;
  value: Record<string, unknown>;
}

export function paramOn(rows: readonly ParameterRow[], key: string, on: LocalDate): Record<string, unknown> | null {
  const hit = rows
    .filter((r) => r.key === key && compareDates(r.effective_from, on) <= 0 && (r.effective_to === null || compareDates(on, r.effective_to) <= 0))
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
  return hit?.value ?? null;
}

export class MissingParameter extends Error {
  constructor(readonly key: string, readonly on: LocalDate) {
    super(`PARAMETER_VERSION_MISSING: ${key} on ${on}`);
  }
}

function need(rows: readonly ParameterRow[], key: string, on: LocalDate): Record<string, unknown> {
  const v = paramOn(rows, key, on);
  if (!v) throw new MissingParameter(key, on);
  return v;
}

export function salarioBase(rows: readonly ParameterRow[], on: LocalDate): Money {
  return Money.of(String(need(rows, "SALARIO_BASE", on).amount));
}

/** Months or fraction between due and payment dates (ceil of calendar months). */
export function monthsOrFraction(due: LocalDate, paid: LocalDate): number {
  if (compareDates(paid, due) <= 0) return 0;
  let n = 0;
  while (compareDates(addMonths(due, n), paid) < 0) n++;
  return n;
}

/** Simple interest at the daily-varying annual rate, days (due, until]. */
export function lateInterest(rows: readonly ParameterRow[], principal: Money, due: LocalDate, until: LocalDate): Money {
  if (compareDates(until, due) <= 0 || principal.isZero()) return Money.zero(principal.currency);
  // Walk the interval in segments of constant rate.
  let total = Money.zero(principal.currency);
  let cursor = due; // exclusive
  while (compareDates(cursor, until) < 0) {
    const day = addDays1(cursor);
    const rateRow = rows
      .filter((r) => r.key === "LATE_INTEREST_ANNUAL_RATE" && compareDates(r.effective_from, day) <= 0 && (r.effective_to === null || compareDates(day, r.effective_to) <= 0))
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
    if (!rateRow) throw new MissingParameter("LATE_INTEREST_ANNUAL_RATE", day);
    const segEnd = rateRow.effective_to && compareDates(rateRow.effective_to, until) < 0 ? rateRow.effective_to : until;
    const days = daysBetween(cursor, segEnd);
    total = total.add(principal.times(String(rateRow.value.rate)).times(days).dividedBy(365));
    cursor = segEnd;
  }
  return total;
}

function addDays1(d: LocalDate): LocalDate {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + 1);
  return t.toISOString().slice(0, 10);
}

export interface LateEstimateInput {
  dueDate: LocalDate;
  /** Used for anything not yet filed/paid (the estimate "if done today"). */
  asOf: LocalDate;
  /** Actual filing date, or null if not filed yet. */
  filedOn: LocalDate | null;
  /** Actual payment date, or null if not paid yet. */
  paidOn: LocalDate | null;
  /** Self-assessed tax owed. null = unknown -> surcharge/interest not estimated. */
  tax: Money | null;
  hasPayment: boolean;
  /** Voluntary regularization before Hacienda acts (art. 88 a). */
  voluntary: boolean;
  /** The fine is self-assessed and paid when curing (80% instead of 75%). */
  fineSelfAssessedAndPaid: boolean;
}

export interface LateEstimate {
  fine: Money;
  fineReduction: Money;
  surcharge: Money;
  surchargeMonths: number;
  interest: Money;
  /** Tax + net fine + surcharge + interest. */
  total: Money;
  amountKnown: boolean;
  notes: string[];
}

export function estimateLate(rows: readonly ParameterRow[], i: LateEstimateInput): LateEstimate {
  const notes: string[] = [];
  const filed = i.filedOn ?? i.asOf;
  const paid = i.paidOn ?? i.asOf;
  // Salario base in force on the day after the deadline (the infraction date) [verify].
  const infractionDay = addDays1(i.dueDate);
  let fine = Money.zero();
  if (compareDates(filed, i.dueDate) > 0) {
    const f = need(rows, "LATE_FILING_FINE_SALARIOS", infractionDay);
    fine = salarioBase(rows, infractionDay).times(String(f.salarios_base));
  }
  let fineReduction = Money.zero();
  if (!fine.isZero() && i.voluntary) {
    const pct = i.fineSelfAssessedAndPaid ? "0.80" : "0.75";
    fineReduction = fine.times(pct);
    notes.push(`Reducción art. 88 CNPT de ${pct === "0.80" ? "80" : "75"}% sobre la multa (no aplica a recargos ni intereses).`);
  }
  let surcharge = Money.zero();
  let interest = Money.zero();
  let months = 0;
  const payLate = i.hasPayment && compareDates(paid, i.dueDate) > 0;
  if (payLate && i.tax && i.tax.isPositive()) {
    months = monthsOrFraction(i.dueDate, paid);
    const monthly = String(need(rows, "LATE_PAYMENT_SURCHARGE_MONTHLY", infractionDay).rate);
    const cap = String(need(rows, "LATE_PAYMENT_SURCHARGE_CAP", infractionDay).rate);
    const rate = dec(monthly).times(months);
    surcharge = i.tax.times((rate.gt(cap) ? dec(cap) : rate).toString());
    interest = lateInterest(rows, i.tax, i.dueDate, paid);
  } else if (payLate && !i.tax) {
    notes.push("Monto del impuesto desconocido: no se estiman recargos ni intereses.");
  }
  const fineNet = fine.sub(fineReduction).round(2);
  const surchargeR = surcharge.round(2);
  const interestR = interest.round(2);
  const total = (i.tax && i.hasPayment ? i.tax : Money.zero()).add(fineNet).add(surchargeR).add(interestR);
  return {
    fine: fine.round(2),
    fineReduction: fineReduction.round(2),
    surcharge: surchargeR,
    surchargeMonths: months,
    interest: interestR,
    total: total.round(2),
    amountKnown: Boolean(i.tax),
    notes,
  };
}

/** Art. 83 exposure for an omitted informative return (range when income is unknown). */
export function estimateInformativeOmission(rows: readonly ParameterRow[], asOf: LocalDate, priorYearGrossIncome: Money | null): { min: Money; max: Money } {
  const sb = salarioBase(rows, asOf);
  const min = sb.times(String(need(rows, "INFORMATIVE_OMISSION_MIN_SALARIOS", asOf).salarios_base));
  const max = sb.times(String(need(rows, "INFORMATIVE_OMISSION_MAX_SALARIOS", asOf).salarios_base));
  if (!priorYearGrossIncome) {
    const unknown = sb.times(String(need(rows, "INFORMATIVE_OMISSION_UNKNOWN_INCOME_SALARIOS", asOf).salarios_base));
    return { min: unknown.round(2), max: unknown.round(2) };
  }
  const v = priorYearGrossIncome.times(String(need(rows, "INFORMATIVE_OMISSION_RATE", asOf).rate));
  const clamped = v.max(min).min(max);
  return { min: clamped.round(2), max: clamped.round(2) };
}

/**
 * Exposure for an overdue obligation "if resolved today": fine + surcharge +
 * interest, WITHOUT the art. 88 reduction (shown separately). Tax itself is
 * excluded. null when nothing is late.
 */
export function overduePenalty(
  rows: readonly ParameterRow[],
  o: { due_date: LocalDate; filing_status: string; payment_status: string; has_payment: boolean; amount_due: string | null; expected_amount: string | null; amount_paid?: string | null },
  today: LocalDate,
): { exposure: Money; withVoluntaryReduction: Money; amountKnown: boolean } | null {
  if (compareDates(today, o.due_date) <= 0) return null;
  const notFiled = o.filing_status === "NOT_FILED";
  const unpaid = o.has_payment && ["PENDING", "PARTIALLY_PAID"].includes(o.payment_status);
  if (!notFiled && !unpaid) return null;
  const amount = o.amount_due ?? o.expected_amount;
  const tax = amount === null ? null : Money.fromDb(amount).sub(Money.fromDb(o.amount_paid ?? "0")).max(Money.zero());
  const base = { dueDate: o.due_date, asOf: today, filedOn: notFiled ? null : o.due_date, paidOn: unpaid ? null : o.due_date, tax, hasPayment: o.has_payment };
  try {
    const plain = estimateLate(rows, { ...base, voluntary: false, fineSelfAssessedAndPaid: false });
    const reduced = estimateLate(rows, { ...base, voluntary: true, fineSelfAssessedAndPaid: true });
    const taxPart = tax && o.has_payment ? tax : Money.zero();
    return { exposure: plain.total.sub(taxPart), withVoluntaryReduction: reduced.total.sub(taxPart), amountKnown: Boolean(tax) };
  } catch (e) {
    if (e instanceof MissingParameter) return null;
    throw e;
  }
}
